import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { headerStyle } from "../question.style";
import { Edit } from "@mui/icons-material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAppSelector } from "../../../Redux/hooks";
import { useEffect, useState } from "react";
import { permissions } from "../../../utils/roles";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import http from "../../../utils/http";
import { errorToastMessage } from "../../../utils/toast";
import React from "react";
import { Languages } from "../../../Redux/reducers/studySlice";
import BackButton from "../../Common/UI/BackButton";

type props = {
  type: "platform" | "study";
  languages: Languages[];
};
const QbPreviewHeader: React.FC<props> = ({ type, languages }) => {
  const { id: studyId, formId } = useParams();
  const questionName = useAppSelector((state) => state.question.questionName);
  const permissionsArray = useAppSelector(
    (state) => state.study.studyPermissions
  );
  const studyStatus = useAppSelector((state) => state.study.status);
  const formCategory = useAppSelector((state) => state.question.formCategory);
  const formStatus = useAppSelector((state) => state.question.status);
  const [editable, setEditable] = useState(false);
  const [searchParams] = useSearchParams();
  const lang = searchParams.get("lang") ?? "";

  const navigate = useNavigate();

  useEffect(() => {
    if (type === "platform") {
      setEditable(!(formCategory === "screening" && formStatus !== "draft"));
    } else {
      if (formCategory && formStatus) {
        const hasAccess = permissionsArray?.includes(permissions.studyDesigner);
        setEditable(
          hasAccess &&
            (formCategory === "screening"
              ? formStatus === "draft"
              : studyStatus === "not_live")
        );
      } else {
        setEditable(false);
      }
    }
  }, [formCategory, formStatus, permissionsArray, studyStatus, type]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleEdit = (lang: string | undefined) => {
    if (!lang || lang === "en") {
      navigate(`../edit`, { relative: "path" });
    } else {
      navigate(`../edit-translation?lang=${lang}`, { relative: "path" });
    }
  };

  const handleTranslation = (lang: string) => {
    navigate(`../preview?lang=${lang}`, { relative: "path" });
  };

  const exportForm = async () => {
    try {
      const prefix =
        type === "platform" ? "/admin/registry/" : `/study/${studyId}/`;
      let httpUrl = `${prefix}forms/${formId}/template`;
      if (lang && lang !== "en") httpUrl += `?lang=${lang}`;
      const res = await http.get(httpUrl, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${questionName}${lang ? `-${lang}` : ""}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      try {
        const text = await err.response.data.text();
        const errorData = JSON.parse(text);
        errorToastMessage(errorData || "Export failed");
      } catch (e) {
        errorToastMessage(err as Error);
      }
    }
  };

  const exportCsv = async () => {
    try {
      const prefix =
        type === "platform" ? "/admin/registry/" : `/study/${studyId}/`;
      let httpUrl = `${prefix}forms/${formId}/export-csv`;
      const res = await http.get(httpUrl, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${questionName}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      try {
        const text = await err.response.data.text();
        const errorData = JSON.parse(text);
        errorToastMessage(errorData || "Export failed");
      } catch (e) {
        errorToastMessage(err as Error);
      }
    }
  };

  return (
    <Box sx={headerStyle}>
      <BackButton onClick={handleBack} sx={{ mr: 1 }} />
      <Typography
        variant="h2"
        color="text.primary"
        sx={{
          flex: 1,
          minWidth: "0px",
        }}
        noWrap
      >
        {questionName || "Form Preview"}
      </Typography>
      <span style={{ marginLeft: "auto" }}></span>
      <Box sx={{ display: "flex", alignItems: "enter" }}>
        {languages.length > 1 && (
          <FormControl sx={{ width: "120px", mr: 2 }}>
            <InputLabel id="language-label">Language</InputLabel>
            <Select
              value={lang || "en"}
              onChange={(e) => {
                handleTranslation(e.target.value);
              }}
              id="lang"
              label="Language"
              labelId="language-label"
              sx={{ height: "40px" }}
            >
              {languages.length > 0 ? (
                languages.map((lang) => (
                  <MenuItem key={lang.languageCode} value={lang.languageCode}>
                    {lang.languageLabel}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No languages available</MenuItem>
              )}
            </Select>
          </FormControl>
        )}

        <Button
          startIcon={<FileDownloadIcon />}
          onClick={exportCsv}
          sx={{ mr: 2 }}
          variant="outlined"
        >
          Export CSV
        </Button>
        {languages.length > 1 && (
          <Button
            startIcon={<FileDownloadIcon />}
            onClick={exportForm}
            sx={{ mr: 2 }}
            variant="outlined"
          >
            Export Template
          </Button>
        )}
        {editable && (
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => handleEdit(lang)}
          >
            Edit
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default QbPreviewHeader;
